import { Box } from '@mui/material';
import noop from 'lodash/noop';
import { useCallback } from 'react';

import { StudentSection } from 'layout/MainLayout/components/Sidebar/components/components/StudentSection';
import { UserSection } from 'layout/MainLayout/components/Sidebar/components/components/UserSection';
import { UserSectionWrapper } from 'layout/MainLayout/components/Sidebar/components/components/UserSectionWrapper';
import { useParentExtraNavigationItems } from 'layout/MainLayout/components/Sidebar/hooks';
import { sidebarActions } from 'layout/MainLayout/components/Sidebar/store';
import { useAppDispatch, useAppSelector } from 'shared/hooks/store';
import { useUserRole } from 'shared/hooks/user';
import { UserRole } from 'shared/types';

import { DrawerWrapper } from './components/DrawerWrapper';
import { NavigationSection } from './components/NavigationSection';
import { SchoolSection } from './components/SchoolSection';
import { ToggleButton } from './components/ToggleButton';
import {
  SIDEBAR_DESKTOP_TEST_ID,
  PARENT_SECTION_WRAPPER_TEST_ID,
} from './constants';

import type { NavigationItem } from 'layout/MainLayout/components/Sidebar/types';
import type { FC } from 'react';
import type { AnalyticsEvent } from 'shared/constants/analytics';

interface SidebarDesktopProps {
  navigationItems: NavigationItem[];
  onActive?(event: AnalyticsEvent): void;
}

export const SidebarDesktop: FC<SidebarDesktopProps> = ({
  navigationItems,
  onActive = noop,
}) => {
  const role = useUserRole();
  const dispatch = useAppDispatch();
  const { opened } = useAppSelector(state => state.sidebar);
  const parentExtraNavigationItems = useParentExtraNavigationItems();

  const toggleSidebarState = useCallback(() => {
    dispatch(sidebarActions.setSidebarState(!opened));
  }, [dispatch, opened]);

  return (
    <Box position="relative" height="100%">
      <DrawerWrapper
        data-testid={SIDEBAR_DESKTOP_TEST_ID}
        variant="permanent"
        open={opened}
      >
        <SchoolSection opened={opened} />
        <StudentSection opened={opened} />
        <Box sx={{ minHeight: 265, alignSelf: 'flex-start' }}>
          <NavigationSection
            opened={opened}
            navigationItems={navigationItems}
            onActive={onActive}
          />
        </Box>
        {role === UserRole.Parent && (
          <UserSectionWrapper
            data-testid={PARENT_SECTION_WRAPPER_TEST_ID}
            opened={opened}
          >
            <UserSection />
            {parentExtraNavigationItems && (
              <NavigationSection
                opened={opened}
                navigationItems={parentExtraNavigationItems}
                onActive={onActive}
              />
            )}
          </UserSectionWrapper>
        )}
      </DrawerWrapper>
      <ToggleButton opened={opened} onClick={toggleSidebarState} />
    </Box>
  );
};
